import type {
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
} from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError, AxiosRequestConfig } from "axios";

import type { IQuery } from "@citrine/client";
import ClientBase, { useMergeQuery } from "@citrine/client";
import { STATIC_QUERY_OPTIONS, queryClient } from "@citrine/client/utils";

import type { ExecutionCandidateHistory } from "../execution-candidate-history";
import type { MaterialModificationTemplateResponse } from "../modules/common";

import type {
  CandidateModificationTemplate,
  ExecutionCandidate,
} from "./execution-candidate";
import { getCandidateName } from "./execution-candidate";

function getFindOption(
  options: Omit<
    UseQueryOptions<ExecutionCandidate[], AxiosError>,
    "queryKey" | "queryFn"
  > = {},
): Omit<
  UseQueryOptions<ExecutionCandidate[], AxiosError>,
  "queryKey" | "queryFn"
> {
  return {
    ...STATIC_QUERY_OPTIONS,
    ...options,
  };
}

export class ExecutionCandidateClient extends ClientBase<ExecutionCandidate> {
  delete!: never;
  patch!: never;
  update!: never;
  useDelete!: never;
  usePatch!: never;
  useUpdate!: never;
  protected readonly historyEndpoint: string;

  protected constructor(
    readonly projectId: string,
    readonly workflowId: string,
    readonly executionId: string,
  ) {
    super({
      endpoint: `/api/v1/projects/${projectId}/design-workflows/${workflowId}/executions/${executionId}/candidates`,
      findField: "response",
    });
    this.historyEndpoint = `/api/v1/projects/${projectId}/design-workflows/${workflowId}/executions/${executionId}/candidate-histories`;
  }

  static getInstance(
    projectId: string,
    workflowId: string,
    executionId: string,
  ) {
    return new this(projectId, workflowId, executionId);
  }

  useCreate(
    options?: Omit<
      UseMutationOptions<ExecutionCandidate, AxiosError, ExecutionCandidate>,
      "mutationFn"
    >,
  ) {
    return useMutation({
      ...options,
      mutationFn: async (candidate) => {
        const result = await super.create(candidate);
        queryClient.removeQueries({
          queryKey: this.getQueryCacheKey({}),
          exact: false,
        });
        return result;
      },
    });
  }

  useFind(
    query: IQuery = {},
    options?: UseQueryOptions<ExecutionCandidate[], AxiosError>,
  ) {
    return super.useFind(query, getFindOption(options));
  }

  useFindAll(
    query: IQuery = {},
    options?: UseQueryOptions<ExecutionCandidate[], AxiosError>,
  ) {
    return super.useFindAll(query, getFindOption(options));
  }

  useFindAllBatched(
    query: IQuery = {},
    options?: UseQueryOptions<ExecutionCandidate[], AxiosError>,
  ) {
    return super.useFindAllBatched(query, getFindOption(options));
  }

  getPredictionTemplate(candidateId: string) {
    return this.request<MaterialModificationTemplateResponse>({
      url: `${this.endpoint}/${candidateId}/modification-template`,
      method: "GET",
    });
  }

  useGetPredictionTemplate(candidateId: string) {
    return useQuery({
      queryKey: [
        ...this.getModelCacheKey(candidateId),
        "modification-template",
      ],
      queryFn: () => this.getPredictionTemplate(candidateId),
    });
  }

  useGetModificationTemplate(candidateId: string) {
    const predictionTemplateQuery = this.useGetPredictionTemplate(candidateId);
    const candidateQuery = this.useGet(candidateId);
    return useMergeQuery<
      [MaterialModificationTemplateResponse, ExecutionCandidate],
      CandidateModificationTemplate
    >(
      [predictionTemplateQuery, candidateQuery],
      ([predictionTemplate, candidate]) => {
        return {
          candidate_id: candidateId,
          descriptors: predictionTemplate.descriptors,
          material: predictionTemplate.predict_request.material,
          material_id: predictionTemplate.predict_request.material_id,
          name: getCandidateName(candidate),
        };
      },
    );
  }

  pinExecutionCandidate(id: string) {
    return this.requestWithRefetch(id, {
      method: "PUT",
      url: `${this.endpoint}/${id}/pin`,
    });
  }

  unpinExecutionCandidate(id: string) {
    return this.requestWithRefetch(id, {
      method: "PUT",
      url: `${this.endpoint}/${id}/unpin`,
    });
  }

  useUpdatePinnedField(
    options?: Omit<
      UseMutationOptions<
        ExecutionCandidate,
        AxiosError,
        { id: string; pinned: boolean }
      >,
      "mutationFn"
    >,
  ) {
    return useMutation({
      ...options,
      mutationFn: (data) =>
        data.pinned
          ? this.pinExecutionCandidate(data.id)
          : this.unpinExecutionCandidate(data.id),
    });
  }

  hideExecutionCandidate(id: string) {
    return this.requestWithRefetch(id, {
      method: "PUT",
      url: `${this.endpoint}/${id}/hide`,
    });
  }

  unhideExecutionCandidate(id: string) {
    return this.requestWithRefetch(id, {
      method: "PUT",
      url: `${this.endpoint}/${id}/unhide`,
    });
  }

  useUpdateHiddenField(
    options?: Omit<
      UseMutationOptions<
        ExecutionCandidate,
        AxiosError,
        { id: string; hidden: boolean }
      >,
      "mutationFn"
    >,
  ) {
    return useMutation({
      ...options,
      mutationFn: (data) =>
        data.hidden
          ? this.hideExecutionCandidate(data.id)
          : this.unhideExecutionCandidate(data.id),
    });
  }

  updateNameField({ id, name }: { id: string; name: string }) {
    return this.requestWithRefetch(id, {
      method: "PUT",
      url: `${this.endpoint}/${id}/rename`,
      data: { name },
    });
  }

  useUpdateNameField(
    options?: Omit<
      UseMutationOptions<
        ExecutionCandidate,
        AxiosError,
        { id: string; name: string }
      >,
      "mutationFn"
    >,
  ) {
    return useMutation({
      ...options,
      mutationFn: (data) => this.updateNameField(data),
    });
  }

  addComment({ id, message }: { id: string; message: string }) {
    return this.requestWithRefetch(id, {
      method: "POST",
      url: `${this.endpoint}/${id}/comments`,
      data: { message },
    });
  }

  useAddComment(
    options?: Omit<
      UseMutationOptions<
        ExecutionCandidate,
        AxiosError,
        { id: string; message: string }
      >,
      "mutationFn"
    >,
  ) {
    return useMutation({
      ...options,
      mutationFn: (data) => this.addComment(data),
    });
  }

  // need to override the cache keys because it overlaps with the candidate-histories
  getModelCacheKey(id: string): QueryKey {
    return [...super.getModelCacheKey(id), "execution-candidate"];
  }

  protected findConfig(query?: IQuery): AxiosRequestConfig {
    return {
      ...super.findConfig(query),
      url: this.historyEndpoint,
    };
  }

  protected getConfig(id?: string): AxiosRequestConfig {
    return {
      ...super.getConfig(id),
      url: `${this.historyEndpoint}/${id}`,
    };
  }

  protected toModel(
    payload: ExecutionCandidate | ExecutionCandidateHistory,
  ): ExecutionCandidate {
    return super.toModel(
      "terminal" in payload.material
        ? {
            type: "pioneer",
            ...payload,
            primary_score: Number(payload.primary_score),
            material: {
              ...payload.material.terminal,
              vars: payload.material.sub_materials.reduce(
                (acc, material) => {
                  return { ...material.vars, ...acc };
                },
                payload.material.mixture_summary.flat
                  ? {
                      ["Flat Formulation"]:
                        payload.material.mixture_summary.flat,
                      ...payload.material.terminal.vars,
                    }
                  : payload.material.terminal.vars,
              ),
            },
            material_id: payload.material.terminal.identifiers.id,
            identifiers: payload.material.terminal.identifiers.external,
          }
        : payload,
    );
  }

  /**
   * Refetches the candidate after the request is made
   * Used because patch requests return execution candidates and we
   * need candidate histories
   * @param id - The id of the candidate
   * @param config - The axios request config
   * @returns The updated candidate
   */
  private async requestWithRefetch(
    id: string,
    config: AxiosRequestConfig,
  ): Promise<ExecutionCandidate> {
    await this.request(config);
    const freshCandidate = await this.get(id);
    this.updateCachedCandidatesEntries(freshCandidate);

    return freshCandidate;
  }

  private updateCachedCandidatesEntries(result: ExecutionCandidate) {
    this.invalidateAfterMutation(result.id);
    this.addModelToCache(result);

    const cacheKey = this.getQueryCacheKey();
    queryClient.setQueriesData(
      { queryKey: cacheKey },
      (previousData: ExecutionCandidate[] | undefined) => {
        return previousData
          ? previousData.map((cachedCandidate) =>
              cachedCandidate.id === result.id ? result : cachedCandidate,
            )
          : previousData;
      },
    );
  }
}
